import type { ResumeData, ResumeFormat } from './types';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

// parse + normalize 一步到位：旧数据 format 缺省时兜底 'md'；非法 format 拒绝
function parseResumeData(value: unknown): ResumeData | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const obj = value as Record<string, unknown>;
  if (
    typeof obj.content !== 'string' ||
    typeof obj.showToc !== 'boolean' ||
    typeof obj.hidden !== 'boolean'
  ) {
    return null;
  }
  if (obj.format !== undefined && obj.format !== 'md' && obj.format !== 'html') {
    return null;
  }
  const format: ResumeFormat = obj.format === 'html' ? 'html' : 'md';
  return { content: obj.content, showToc: obj.showToc, hidden: obj.hidden, format };
}

export async function fetchResume(): Promise<ResumeData | null> {
  const res = await fetch('/api/resume');
  if (!res.ok) {
    throw new ApiError(res.status, `fetchResume failed: ${res.status}`);
  }
  let data: unknown;
  try {
    data = await res.json();
  } catch (err) {
    console.error('fetchResume: invalid JSON', err);
    return null;
  }
  const parsed = parseResumeData(data);
  if (parsed === null) {
    console.error('fetchResume: schema mismatch', data);
    return null;
  }
  return parsed;
}

export async function putResume(data: ResumeData, secret: string): Promise<void> {
  const res = await fetch('/api/resume', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${secret}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new ApiError(res.status, `putResume failed: ${res.status}`);
  }
}

// 站点级开关（隐藏简历 / 显示导航）即时持久化：PATCH 仅改 KV 里的 flag，
// 不写历史、不动 content —— 对应"大门开关，立即生效、无需保存"。
export async function updateResumeSettings(
  secret: string,
  settings: { showToc?: boolean; hidden?: boolean },
): Promise<void> {
  const res = await fetch('/api/resume', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${secret}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(settings),
  });
  if (!res.ok) {
    throw new ApiError(res.status, `updateResumeSettings failed: ${res.status}`);
  }
}

// ok          — 密码正确
// invalid     — 密码错误（401）
// rate-limited — 同 IP 失败次数过多（429），不代表密码本身错
// error       — 网络异常或其他状态，无法判定
export type VerifyResult = 'ok' | 'invalid' | 'rate-limited' | 'error';

export async function verifySecret(secret: string): Promise<VerifyResult> {
  let res: Response;
  try {
    res = await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret }),
    });
  } catch {
    return 'error';
  }
  if (res.status === 200) {
    return 'ok';
  }
  if (res.status === 401) {
    return 'invalid';
  }
  if (res.status === 429) {
    return 'rate-limited';
  }
  return 'error';
}

const HISTORY_TITLE_MAX = 80;

// 与后端 deriveHistoryTitle 对齐：取内容首段非空文字作默认标题。
// 兜底用——正常情况后端 GET /api/history 已回填 title；抽屉里清空标题也复用它。
export function deriveHistoryTitle(content: string): string {
  const firstLine = content.split('\n').find((l) => l.trim() !== '');
  if (firstLine === undefined) {
    return '（空白）';
  }
  const trimmed = firstLine.trim();
  return trimmed.length > HISTORY_TITLE_MAX ? trimmed.slice(0, HISTORY_TITLE_MAX) : trimmed;
}

export interface HistoryEntry {
  content: string;
  savedAt: number;
  format: ResumeFormat;
  // 历史列表展示标题；后端缺省时由 content 回填，可被用户改写
  title: string;
}

function parseHistoryEntry(value: unknown): HistoryEntry | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.content !== 'string' || typeof obj.savedAt !== 'number') {
    return null;
  }
  if (obj.format !== undefined && obj.format !== 'md' && obj.format !== 'html') {
    return null;
  }
  const format: ResumeFormat = obj.format === 'html' ? 'html' : 'md';
  const title =
    typeof obj.title === 'string' && obj.title.trim() !== ''
      ? obj.title.slice(0, HISTORY_TITLE_MAX)
      : deriveHistoryTitle(obj.content);
  return { content: obj.content, savedAt: obj.savedAt, format, title };
}

export async function fetchHistory(secret: string): Promise<HistoryEntry[]> {
  const res = await fetch('/api/history', {
    headers: { Authorization: `Bearer ${secret}` },
  });
  if (!res.ok) {
    throw new ApiError(res.status, `fetchHistory failed: ${res.status}`);
  }
  const data = (await res.json()) as unknown;
  if (!Array.isArray(data)) {
    return [];
  }
  const entries: HistoryEntry[] = [];
  for (const item of data) {
    const parsed = parseHistoryEntry(item);
    if (parsed !== null) {
      entries.push(parsed);
    }
  }
  return entries;
}

export async function deleteHistory(secret: string, savedAt: number): Promise<void> {
  const res = await fetch(`/api/history?savedAt=${encodeURIComponent(savedAt)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${secret}` },
  });
  if (!res.ok) {
    throw new ApiError(res.status, `deleteHistory failed: ${res.status}`);
  }
}

// "使用此版本"：把 history 中 savedAt=X 的条目提升为线上版本（不新增条目）
// 后端返回新的 ResumeData（content/format 来自被选条目，showToc/hidden 沿用当前线上）
export async function promoteHistory(secret: string, savedAt: number): Promise<ResumeData> {
  const res = await fetch('/api/history', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${secret}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ savedAt }),
  });
  if (!res.ok) {
    throw new ApiError(res.status, `promoteHistory failed: ${res.status}`);
  }
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new ApiError(0, 'promoteHistory: invalid JSON');
  }
  if (typeof data !== 'object' || data === null) {
    throw new ApiError(0, 'promoteHistory: invalid response');
  }
  const obj = data as Record<string, unknown>;
  const parsed = parseResumeData(obj.resume);
  if (parsed === null) {
    throw new ApiError(0, 'promoteHistory: invalid resume');
  }
  return parsed;
}

// 改写某条历史的标题：PATCH /api/history { savedAt, title }，立即持久化。
// 传空白标题，后端会回落到内容派生的默认标题。
export async function updateHistoryTitle(
  secret: string,
  savedAt: number,
  title: string,
): Promise<void> {
  const res = await fetch('/api/history', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${secret}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ savedAt, title }),
  });
  if (!res.ok) {
    throw new ApiError(res.status, `updateHistoryTitle failed: ${res.status}`);
  }
}
